import { useMemo } from "react";
import { useSettingsStore } from "../../../store/settingsStore.jsx";
import { getIndicatorMinMax, getPriceMinMax } from "../utils/domains";
import {
  groupIndicatorsByPanel,
  getPanelIndicators,
} from "../../../utils/indicatorPanelAssignment.js";
import { getCompareYExtents } from "../../../store/compare";

const ChartConfigProvider = ({
  chartType,
  chartSymbol,
  indicators,
  isCompareMode = false,
  comparisonSymbols = [],
  children,
}) => {
  // Get Y-axis drag setting from the settings store
  const yAxisDrag = useSettingsStore((state) => state.yAxisDrag);

  // PHASE 3: Group indicators by effective panel (considers series-as-source)
  const panelAssignments = useMemo(
    () => groupIndicatorsByPanel(indicators),
    [indicators],
  );

  // Memoize indicator buckets to prevent unnecessary recalculations
  const separateIndicators = useMemo(() => {
    // Each separate panel has a primary indicator
    return panelAssignments.separatePanels.map((panel) => panel.primary);
  }, [panelAssignments]);

  const mainIndicators = useMemo(
    () => panelAssignments.mainPanel,
    [panelAssignments],
  );

  // Memoize indicator IDs to prevent unnecessary recalculations
  const indicatorsKey = useMemo(
    () => indicators.map((ind) => `${ind.id}-${ind.requireNewAxis}`).join(","),
    [indicators],
  );

  // Main chart config with pure domain - use indicatorsKey for better memoization
  const mainChartConfig = useMemo(() => {
    // yExtentsCalculator receives full plotData array and calculates domain
    const yExtentsCalculator = ({ plotData }) => {
      const dataArr = plotData;

      // In compare mode, use price-based extents (Y-axis shows main symbol prices)
      // The tick formatter converts prices to percentages using the baseline
      if (isCompareMode && comparisonSymbols.length > 0) {
        return getCompareYExtents(dataArr);
      }

      let { min, max } = getPriceMinMax(dataArr);

      // Include main indicators in domain calculation
      mainIndicators.forEach((indicator) => {
        if (indicator?.includeInMainExtent === false) return;

        const { min: indMin, max: indMax } = getIndicatorMinMax(
          dataArr,
          indicator,
        );

        min = Math.min(min, indMin);
        max = Math.max(max, indMax);
      });

      // Return exact calculated range without additional margins
      return [min, max];
    };

    const config = {
      id: 1,
      type: "main",
      index: 0,
      yExtents: (d) => {
        // In compare mode, return percentage values
        if (isCompareMode) {
          const percentValues = [d.main_percentChange];
          comparisonSymbols.forEach(({ symbol }) => {
            const key = `compare_${symbol}`;
            if (d[key] !== undefined && d[key] !== null) {
              percentValues.push(d[key]);
            }
          });
          const validValues = percentValues.filter(
            (v) => v !== undefined && v !== null,
          );
          if (validValues.length > 0) {
            return [Math.min(...validValues), Math.max(...validValues)];
          }
          return [-10, 10];
        }

        // Dummy function - actual calculation done by yExtentsCalculator
        if (d && d.high !== undefined && d.low !== undefined) {
          return [d.low, d.high];
        }
        return [0, 1];
      },
      yExtentsCalculator,
      padding: { top: 25, bottom: 10 },
      yPan: yAxisDrag, // Enable Y-axis dragging based on settings
      chartType,
      chartSymbol,
      indicators: mainIndicators,
      isCompareMode,
    };

    return config;
  }, [
    chartType,
    chartSymbol,
    mainIndicators,
    indicatorsKey,
    yAxisDrag,
    isCompareMode,
    comparisonSymbols,
  ]);

  // Indicator charts - simplified y-extent calculation
  // PHASE 3: Each panel includes primary indicator + overlay indicators (series-as-source)
  const indicatorChartConfigs = useMemo(() => {
    const configs = panelAssignments.separatePanels.map((panel, i) => {
      const primaryIndicator = panel.primary;
      const allPanelIndicators = getPanelIndicators(panel);

      // yExtentsCalculator receives full plotData array and calculates domain
      const yExtentsCalculator = ({ plotData }) => {
        // If primary indicator has custom yExtents function (copied from registry), use it
        if (
          primaryIndicator.yExtents &&
          typeof primaryIndicator.yExtents === "function"
        ) {
          return primaryIndicator.yExtents(plotData, primaryIndicator);
        }

        // Calculate domain including all indicators in this panel (primary + overlays)
        let min = Infinity;
        let max = -Infinity;

        allPanelIndicators.forEach((indicator) => {
          const { min: indMin, max: indMax } = getIndicatorMinMax(
            plotData,
            indicator,
          );
          min = Math.min(min, indMin);
          max = Math.max(max, indMax);
        });

        // Fallback if no valid data
        if (!isFinite(min) || !isFinite(max)) {
          const { min: primaryMin, max: primaryMax } = getIndicatorMinMax(
            plotData,
            primaryIndicator,
          );
          return [primaryMin, primaryMax];
        }

        return [min, max];
      };

      return {
        id: i + 2,
        type: "indicator",
        index: i + 1,
        yExtents: [(d) => d], // Dummy array - actual calculation done by yExtentsCalculator
        yExtentsCalculator,
        yPan: yAxisDrag, // Enable Y-axis dragging based on settings
        indicator: primaryIndicator,
        // PHASE 3: Include all indicators in this panel
        indicators: allPanelIndicators,
      };
    });

    return configs;
  }, [panelAssignments, indicatorsKey, yAxisDrag]);

  // Build once, then layout
  const allCharts = useMemo(() => {
    return [mainChartConfig, ...indicatorChartConfigs];
  }, [mainChartConfig, indicatorChartConfigs]);

  // Memoize context value to prevent children re-renders when object reference changes
  const contextValue = useMemo(
    () => ({
      mainChartConfig,
      indicatorChartConfigs,
      allCharts,
      separateIndicators,
      mainIndicators,
      indicatorsKey,
    }),
    [
      mainChartConfig,
      indicatorChartConfigs,
      allCharts,
      separateIndicators,
      mainIndicators,
      indicatorsKey,
    ],
  );

  return children(contextValue);
};

export default ChartConfigProvider;
